import KioskController from "./KioskController";
import { Outlet, NavLink, useLocation } from "react-router-dom";
import { useApp } from "../contexts/AppContext";
import { useAuth } from "../contexts/AuthContext";
import { Home, Activity, HeartPulse, MessagesSquare, User2, WifiOff, Wifi, RefreshCw } from "lucide-react";
import { t } from "../lib/i18n";
import { Logo } from "./Logo";
import { CountryOnboarding } from "./CountryPicker";

const navItems = [
  { to: "/app", end: true, icon: Home, key: "nav_home", testid: "nav-home" },
  { to: "/app/exercises", icon: Activity, key: "nav_exercises", testid: "nav-exercises" },
  { to: "/app/health", icon: HeartPulse, key: "nav_health", testid: "nav-health" },
  { to: "/app/communicate", icon: MessagesSquare, key: "nav_comm", testid: "nav-communicate" },
  { to: "/app/profile", icon: User2, key: "nav_profile", testid: "nav-profile" },
];

export default function Layout() {
  const { lang, online, pending, syncNow, aidantMode, setAidantMode } = useApp();
  const { user } = useAuth();
  const loc = useLocation();
  const onAuth = loc.pathname.startsWith("/auth");

  if (onAuth) return <Outlet />;

  return (
    <div className="bc-shell" data-testid="bc-shell">
      {/* Connection banner */}
      <div
        data-testid="conn-banner"
        className={`px-4 py-2 text-xs font-semibold flex items-center justify-between gap-2 ${
          online ? "bg-[#0A1426] text-white" : "bg-[#D4AF5A] text-[#0A1426]"
        }`}
      >
        <span className="flex items-center gap-2">
          {online ? <Wifi size={14} /> : <WifiOff size={14} />}
          {online
            ? (pending > 0 ? t(lang, "syncing") : t(lang, "online"))
            : t(lang, "offline_count", pending)}
        </span>
        {pending > 0 && online && (
          <button
            data-testid="sync-banner-btn"
            onClick={syncNow}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/20"
          >
            <RefreshCw size={12} /> {t(lang, "sync_now")}
          </button>
        )}
      </div>

      {/* Top: logo + mode toggle */}
      <header className="px-5 pt-4 pb-3 flex items-center justify-between bg-[#F5F3EE]">
        <div className="flex items-center gap-3">
          <Logo size={40} />
          <div>
            <div className="font-[Fraunces] text-[18px] leading-none font-semibold text-[#0A1426]">
              Bombacare
            </div>
            <div className="text-[11px] text-[#0A1426]/70 mt-0.5">
              {user ? user.nom : ""}
            </div>
          </div>
        </div>
        <div
          role="tablist"
          className="bg-white rounded-full p-1 shadow-sm border border-[#E4E0D5] flex items-center"
        >
          <button
            data-testid="mode-patient-btn"
            role="tab"
            aria-selected={!aidantMode}
            onClick={() => setAidantMode(false)}
            className={`px-3 py-1.5 text-xs font-bold rounded-full transition-colors ${
              !aidantMode ? "bg-[#2BA3A0] text-white" : "text-[#0A1426]/70"
            }`}
          >
            {t(lang, "mode_patient")}
          </button>
          <button
            data-testid="mode-aidant-btn"
            role="tab"
            aria-selected={aidantMode}
            onClick={() => setAidantMode(true)}
            className={`px-3 py-1.5 text-xs font-bold rounded-full transition-colors ${
              aidantMode ? "bg-[#0A1426] text-white" : "text-[#0A1426]/70"
            }`}
          >
            {t(lang, "mode_aidant")}
          </button>
        </div>
      </header>

      {aidantMode && (
        <div
          data-testid="aidant-banner"
          className="mx-5 mb-3 px-3 py-2 rounded-xl bg-[#0A1426] text-white text-[12px] leading-snug"
        >
          {t(lang, "aidant_banner")}
        </div>
      )}

      <main className="flex-1 overflow-y-auto bc-scroll px-5 pb-32">
        <Outlet />
        <CountryOnboarding />
        <footer data-testid="bombacare-footer" className="text-center text-[10px] text-[#0A1426]/50 py-3">© Bombacare, tous droits réservés · Outil d'accompagnement, ne remplace pas une consultation.</footer>
      </main>

      {/* Modal/Overlay portal slot — overlays render here so they fit the shell */}
      <div id="bc-modal-root" className="absolute inset-0 pointer-events-none z-[60]" />
      <KioskController />

      {/* Bottom nav */}
      <nav
        data-testid="bottom-nav"
        className="absolute bottom-0 left-0 right-0 bg-white border-t border-[#E4E0D5] px-2 pt-2 pb-safe flex items-stretch justify-between z-50"
        style={{ paddingRight: "max(0.5rem, env(safe-area-inset-right, 0px))" }}
      >
        {navItems.map(({ to, end, icon: Icon, key, testid }) => (
          <NavLink
            key={key}
            to={to}
            end={end}
            data-testid={testid}
            className={({ isActive }) =>
              `flex-1 flex flex-col items-center justify-center gap-1 py-1.5 rounded-xl min-h-[60px] ${
                isActive ? "text-[#2BA3A0]" : "text-[#0A1426]/60"
              }`
            }
          >
            <Icon size={26} strokeWidth={2.2} />
            <span className="text-[11px] font-bold">{t(lang, key)}</span>
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
